/**
 * "Var tappade du poängen?" (pedagogy spec §7.3.8): one `<fieldset>` per
 * problem that lost points, one native checkbox per cause styled as a pill
 * ("Vet inte" last), nothing pre-selected — causes are optional (§13 Q17). An
 * empty saved answer adds a hint, never a choice. Full-mark problems collapse
 * into one line.
 */
import { useId } from "react";
import {
  EXAM_CAUSES,
  lostPoints,
  type CauseMap,
  type ExamCause,
  type WrapperProblem,
} from "@shared/examWrapper";
import { Card } from "@client/components/ui";
import { useLang, useT, useTp } from "@client/i18n";
import { formatList, formatNumber } from "@client/lib/format";
import type { AnswerEntry } from "../types";
import { isBlankAnswer } from "./model";

export interface CausesCardProps {
  problems: readonly WrapperProblem[];
  /** The session's saved answers, for the "no answer entered" hint. */
  answers: Readonly<Record<string, AnswerEntry>>;
  causes: CauseMap;
  onToggle: (questionId: string, cause: ExamCause, on: boolean) => void;
}

/** A pill at least 44 px high with a visible focus ring, in both themes. */
const PILL =
  "inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-full border border-ink-300 px-3 py-2 text-sm text-ink-800 " +
  "has-[:checked]:border-accent-600 has-[:checked]:bg-accent-100 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent-600 has-[:focus-visible]:ring-offset-2 " +
  "dark:border-ink-700 dark:text-ink-100 dark:has-[:checked]:border-accent-400 dark:has-[:checked]:bg-accent-900 dark:has-[:focus-visible]:ring-accent-400 dark:has-[:focus-visible]:ring-offset-ink-950";

export function CausesCard({ problems, answers, causes, onToggle }: CausesCardProps) {
  const t = useT();
  const tp = useTp();
  const { lang } = useLang();
  const lost = problems.filter((problem) => lostPoints(problem) > 0);
  const full = problems.filter((problem) => lostPoints(problem) === 0);

  return (
    <Card
      title={t("exam.wrapper.causes.title")}
      description={lost.length > 0 ? t("exam.wrapper.causes.description") : undefined}
    >
      {lost.length === 0 ? (
        <p className="text-sm text-ink-700 dark:text-ink-200">{t("exam.wrapper.causes.none")}</p>
      ) : (
        <div className="space-y-5">
          {lost.map((problem) => (
            <ProblemCauses
              key={problem.questionId}
              problem={problem}
              blank={isBlankAnswer(answers[problem.questionId])}
              chosen={causes[problem.questionId] ?? []}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
      {full.length > 0 && (
        <p className="mt-4 text-sm text-ink-600 dark:text-ink-400">
          {tp("exam.wrapper.causes.fullMarks", full.length, {
            list: formatList(
              full.map((problem) => String(problem.slot)),
              lang,
            ),
          })}
        </p>
      )}
    </Card>
  );
}

function ProblemCauses({
  problem,
  blank,
  chosen,
  onToggle,
}: {
  problem: WrapperProblem;
  blank: boolean;
  chosen: readonly ExamCause[];
  onToggle: CausesCardProps["onToggle"];
}) {
  const t = useT();
  const { lang } = useLang();
  const hintId = useId();
  const points = (value: number) => formatNumber(value, lang, { maxDecimals: 1 });

  return (
    <fieldset aria-describedby={blank ? hintId : undefined}>
      <legend className="text-sm font-medium text-ink-800 dark:text-ink-100">
        {t("exam.wrapper.causes.legend", {
          slot: problem.slot,
          score: points(problem.score),
          max: points(problem.maxScore),
          chapter: t("exam.wrapper.chapter", { chapter: problem.chapter }),
        })}
      </legend>
      {blank && (
        <p id={hintId} className="mt-1 text-xs text-ink-600 dark:text-ink-400">
          {t("exam.wrapper.causes.blank")}
        </p>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        {EXAM_CAUSES.map((cause) => (
          <label key={cause} className={PILL}>
            <input
              type="checkbox"
              className="h-4 w-4 shrink-0 accent-accent-700 focus:outline-none dark:accent-accent-400"
              checked={chosen.includes(cause)}
              onChange={(event) => onToggle(problem.questionId, cause, event.target.checked)}
            />
            {t(`exam.wrapper.cause.${cause}`)}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
